import React from 'react';
import { motion } from 'framer-motion';
import SafeIcon from '../common/SafeIcon';
import * as FiIcons from 'react-icons/fi';
import { Link } from 'react-router-dom';

const { FiArrowLeft, FiTarget, FiClock, FiLayers, FiAlertTriangle, FiCheckCircle, FiRefreshCw, FiFileText } = FiIcons;

function SystemsGuide() {
  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.5 }}
      className="max-w-4xl mx-auto"
    >
      <div className="mb-8">
        <Link
          to="/"
          className="flex items-center space-x-2 text-gray-600 hover:text-gray-900 transition-colors mb-6 inline-flex"
        >
          <SafeIcon icon={FiArrowLeft} className="text-lg" />
          <span>Back to Dashboard</span>
        </Link>
        <h1 className="text-4xl font-bold text-gray-900 mb-2">Systems to Track & When to Refine</h1>
        <p className="text-xl text-gray-600">
          A comprehensive guide to evaluating and improving your business systems
        </p>
      </div>

      <div className="space-y-12">
        {/* Section 1: Systems to Track */}
        <motion.section
          initial={{ opacity: 0, x: -20 }}
          animate={{ opacity: 1, x: 0 }}
          transition={{ delay: 0.1 }}
          className="bg-white rounded-xl shadow-sm border border-gray-200 p-8"
        >
          <div className="flex items-center space-x-3 mb-6">
            <div className="bg-blue-100 p-3 rounded-lg">
              <SafeIcon icon={FiTarget} className="text-blue-600 text-xl" />
            </div>
            <h2 className="text-2xl font-bold text-gray-900">1. Systems You Can Track & Improve</h2>
          </div>
          
          <p className="text-gray-600 mb-8">
            You can evaluate any system that affects clarity, flow, and efficiency in your business. 
            Here are the key areas to focus on:
          </p>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
            <div>
              <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center">
                <SafeIcon icon={FiLayers} className="text-blue-500 mr-2" />
                Operational Systems
              </h3>
              <ul className="space-y-3 text-gray-600">
                <li className="flex items-start">
                  <span className="text-blue-500 mt-1 mr-2">•</span>
                  <div>
                    <strong>Client Onboarding:</strong> Steps from initial contact to fully onboarded
                  </div>
                </li>
                <li className="flex items-start">
                  <span className="text-blue-500 mt-1 mr-2">•</span>
                  <div>
                    <strong>Lead Capture & Intake:</strong> How inquiries are collected and responded to
                  </div>
                </li>
                <li className="flex items-start">
                  <span className="text-blue-500 mt-1 mr-2">•</span>
                  <div>
                    <strong>Payment Flow & Invoicing:</strong> Ease and speed in sending and receiving payments
                  </div>
                </li>
                <li className="flex items-start">
                  <span className="text-blue-500 mt-1 mr-2">•</span>
                  <div>
                    <strong>Scheduling & Calendar Management:</strong> Setting appointments and reminders
                  </div>
                </li>
                <li className="flex items-start">
                  <span className="text-blue-500 mt-1 mr-2">•</span>
                  <div>
                    <strong>Customer Support Workflow:</strong> How questions or requests are handled
                  </div>
                </li>
                <li className="flex items-start">
                  <span className="text-blue-500 mt-1 mr-2">•</span>
                  <div>
                    <strong>File Organization:</strong> Folder layouts, naming conventions, and accessibility
                  </div>
                </li>
              </ul>
            </div>

            <div>
              <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center">
                <SafeIcon icon={FiFileText} className="text-green-500 mr-2" />
                Marketing Systems
              </h3>
              <ul className="space-y-3 text-gray-600">
                <li className="flex items-start">
                  <span className="text-green-500 mt-1 mr-2">•</span>
                  <div>
                    <strong>Content Creation Workflow:</strong> Planning, drafting, reviewing, posting
                  </div>
                </li>
                <li className="flex items-start">
                  <span className="text-green-500 mt-1 mr-2">•</span>
                  <div>
                    <strong>Social Posting Process:</strong> The steps needed to publish consistently
                  </div>
                </li>
                <li className="flex items-start">
                  <span className="text-green-500 mt-1 mr-2">•</span>
                  <div>
                    <strong>Email Setup & Rhythm:</strong> Templates, sequences, and weekly newsletters
                  </div>
                </li>
                <li className="flex items-start">
                  <span className="text-green-500 mt-1 mr-2">•</span>
                  <div>
                    <strong>Website or Link Flows:</strong> Clarity of user paths and ease of navigation
                  </div>
                </li>
              </ul>

              <h3 className="text-lg font-semibold text-gray-900 mb-4 mt-8 flex items-center">
                <SafeIcon icon={FiRefreshCw} className="text-purple-500 mr-2" />
                Administrative Systems
              </h3>
              <ul className="space-y-3 text-gray-600">
                <li className="flex items-start">
                  <span className="text-purple-500 mt-1 mr-2">•</span>
                  <div>
                    <strong>Data Tracking & Reporting:</strong> How metrics are stored, reviewed, and analyzed
                  </div>
                </li>
                <li className="flex items-start">
                  <span className="text-purple-500 mt-1 mr-2">•</span>
                  <div>
                    <strong>Weekly Planning Routine:</strong> Steps used to map out each week
                  </div>
                </li>
                <li className="flex items-start">
                  <span className="text-purple-500 mt-1 mr-2">•</span>
                  <div>
                    <strong>Offer Delivery Steps:</strong> How services or products are fulfilled
                  </div>
                </li>
                <li className="flex items-start">
                  <span className="text-purple-500 mt-1 mr-2">•</span>
                  <div>
                    <strong>Task Management:</strong> To-do systems, apps, or boards
                  </div>
                </li>
              </ul>
            </div>
          </div>
        </motion.section>

        {/* Section 2: How to Interpret Metrics */}
        <motion.section
          initial={{ opacity: 0, x: -20 }}
          animate={{ opacity: 1, x: 0 }}
          transition={{ delay: 0.2 }}
          className="bg-white rounded-xl shadow-sm border border-gray-200 p-8"
        >
          <div className="flex items-center space-x-3 mb-6">
            <div className="bg-green-100 p-3 rounded-lg">
              <SafeIcon icon={FiClock} className="text-green-600 text-xl" />
            </div>
            <h2 className="text-2xl font-bold text-gray-900">2. How to Interpret Your Metrics</h2>
          </div>

          <div className="space-y-8">
            <div>
              <h3 className="text-lg font-semibold text-gray-900 mb-3">Time Before vs. Time After</h3>
              <ul className="space-y-2 text-gray-600 ml-4">
                <li className="flex items-start">
                  <span className="text-green-500 mt-1 mr-2">•</span>
                  Measures how long a system used to take vs. now
                </li>
                <li className="flex items-start">
                  <span className="text-green-500 mt-1 mr-2">•</span>
                  If time decreases, the system is becoming more efficient
                </li>
                <li className="flex items-start">
                  <span className="text-green-500 mt-1 mr-2">•</span>
                  If time increases, the new process is more complex than the old one
                </li>
              </ul>
            </div>

            <div>
              <h3 className="text-lg font-semibold text-gray-900 mb-3">Steps Before vs. Steps After</h3>
              <ul className="space-y-2 text-gray-600 ml-4">
                <li className="flex items-start">
                  <span className="text-green-500 mt-1 mr-2">•</span>
                  Fewer steps equal a smoother workflow
                </li>
                <li className="flex items-start">
                  <span className="text-green-500 mt-1 mr-2">•</span>
                  If steps increase, examine which steps are unnecessary or redundant
                </li>
                <li className="flex items-start">
                  <span className="text-green-500 mt-1 mr-2">•</span>
                  Large increases in steps typically signal inefficiency or overthinking
                </li>
              </ul>
            </div>

            <div>
              <h3 className="text-lg font-semibold text-gray-900 mb-3">Confusion/Friction Level (1–10)</h3>
              <ul className="space-y-2 text-gray-600 ml-4">
                <li className="flex items-start">
                  <span className="text-green-500 mt-1 mr-2">•</span>
                  <strong>1–3:</strong> Clear and smooth; requires maintenance only
                </li>
                <li className="flex items-start">
                  <span className="text-green-500 mt-1 mr-2">•</span>
                  <strong>4–6:</strong> Usable but needs refinement
                </li>
                <li className="flex items-start">
                  <span className="text-green-500 mt-1 mr-2">•</span>
                  <strong>7–10:</strong> High friction; needs focused attention
                </li>
              </ul>
              <p className="text-gray-600 mt-3 italic">
                Confusion includes emotional friction (stress, overwhelm) and operational friction 
                (uncertainty, backtracking).
              </p>
            </div>
          </div>
        </motion.section>

        {/* Section 3: When to Refine */}
        <motion.section
          initial={{ opacity: 0, x: -20 }}
          animate={{ opacity: 1, x: 0 }}
          transition={{ delay: 0.3 }}
          className="bg-white rounded-xl shadow-sm border border-gray-200 p-8"
        >
          <div className="flex items-center space-x-3 mb-6">
            <div className="bg-yellow-100 p-3 rounded-lg">
              <SafeIcon icon={FiRefreshCw} className="text-yellow-600 text-xl" />
            </div>
            <h2 className="text-2xl font-bold text-gray-900">3. Knowing When to Refine vs. Rebuild</h2>
          </div>

          <div className="bg-yellow-50 border-l-4 border-yellow-400 p-6 mb-8">
            <h3 className="text-lg font-semibold text-yellow-900 mb-3">When to Refine</h3>
            <p className="text-yellow-800 mb-4">
              Refinement focuses on small adjustments—not replacements. Look for these signs:
            </p>
            <ul className="space-y-2 text-yellow-800">
              <li className="flex items-start">
                <SafeIcon icon={FiCheckCircle} className="text-yellow-600 mt-1 mr-2 text-sm flex-shrink-0" />
                Time has improved slightly but still feels heavier than it should
              </li>
              <li className="flex items-start">
                <SafeIcon icon={FiCheckCircle} className="text-yellow-600 mt-1 mr-2 text-sm flex-shrink-0" />
                Steps have reduced a little, but the workflow isn't intuitive yet
              </li>
              <li className="flex items-start">
                <SafeIcon icon={FiCheckCircle} className="text-yellow-600 mt-1 mr-2 text-sm flex-shrink-0" />
                Confusion dropped somewhat, but the system is still clunky
              </li>
              <li className="flex items-start">
                <SafeIcon icon={FiCheckCircle} className="text-yellow-600 mt-1 mr-2 text-sm flex-shrink-0" />
                You understand the problem but haven't fully optimized the solution yet
              </li>
            </ul>
          </div>
        </motion.section>

        {/* Section 4: When to Rebuild */}
        <motion.section
          initial={{ opacity: 0, x: -20 }}
          animate={{ opacity: 1, x: 0 }}
          transition={{ delay: 0.4 }}
          className="bg-white rounded-xl shadow-sm border border-gray-200 p-8"
        >
          <div className="flex items-center space-x-3 mb-6">
            <div className="bg-red-100 p-3 rounded-lg">
              <SafeIcon icon={FiAlertTriangle} className="text-red-600 text-xl" />
            </div>
            <h2 className="text-2xl font-bold text-gray-900">4. When Rebuilding Becomes Necessary (Last Resort)</h2>
          </div>

          <div className="bg-red-50 border-l-4 border-red-400 p-6 mb-6">
            <p className="text-red-800 font-semibold mb-4">
              Rebuilding should be avoided unless the data makes it absolutely clear.
            </p>
            <p className="text-red-800 mb-4">
              A system should be <strong>rebuilt only if ALL of the following conditions are met</strong>:
            </p>
            <ul className="space-y-3 text-red-900">
              <li className="flex items-start">
                <span className="text-red-600 font-bold mr-2">•</span>
                <div>
                  <strong>Confusion remains between 8–10</strong> for two consecutive changes, AND
                </div>
              </li>
              <li className="flex items-start">
                <span className="text-red-600 font-bold mr-2">•</span>
                <div>
                  <strong>Steps have increased by 4 or more</strong> in the last two logged adjustments
                </div>
              </li>
            </ul>
          </div>

          <div className="bg-gray-50 rounded-lg p-6">
            <h3 className="text-lg font-semibold text-gray-900 mb-3">This means:</h3>
            <ul className="space-y-2 text-gray-600 ml-4">
              <li className="flex items-start">
                <span className="text-blue-500 mt-1 mr-2">•</span>
                The system is getting more complicated over time, not simpler
              </li>
              <li className="flex items-start">
                <span className="text-blue-500 mt-1 mr-2">•</span>
                Attempts to refine have backfired or created more friction
              </li>
              <li className="flex items-start">
                <span className="text-blue-500 mt-1 mr-2">•</span>
                The process no longer fits the user's needs and requires a fresh start
              </li>
            </ul>
            
            <div className="mt-6 p-4 bg-blue-50 rounded-lg border border-blue-200">
              <p className="text-blue-900 font-medium">
                Rebuilding is not failure—it is a sign of strong awareness and clarity after testing and tracking.
              </p>
            </div>
          </div>
        </motion.section>
      </div>
    </motion.div>
  );
}

export default SystemsGuide;